import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CreditCardIcon } from "@phosphor-icons/react";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import EmptyState from "../../components/EmptyState";
import { ErrorBanner } from "../../components/crm/Form";
import SubscriptionStatusBadge from "../../components/billing/SubscriptionStatusBadge";
import BillingStatusBadge from "../../components/billing/BillingStatusBadge";
import MrrTile from "../../components/billing/MrrTile";
import { useAuth } from "../../contexts/AuthContext";
import api, { formatApiError } from "../../lib/api";

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "pending", label: "Pending" },
  { value: "canceled", label: "Canceled" },
  { value: "lapsed", label: "Lapsed" },
];

const CADENCE_LABEL = {
  monthly: "/ mo",
  quarterly: "/ qtr",
  annual: "/ yr",
};

function fmtCents(cents) {
  if (cents === null || cents === undefined) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

function isOffice(user) {
  return ["owner", "ops"].includes(user?.role);
}

export default function SubscriptionsList() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const office = isOffice(user);

  const initialStatus = params.get("status") || "";
  const renewingSoon = params.get("renewing_soon") === "1";

  const [list, setList] = useState([]);
  const [status, setStatus] = useState(initialStatus);
  const [err, setErr] = useState("");

  const renewingBefore = useMemo(() => {
    if (!renewingSoon) return null;
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  }, [renewingSoon]);

  const load = async () => {
    setErr("");
    try {
      const params = {};
      if (status) params.status = status;
      if (renewingBefore) {
        params.renewing_before = renewingBefore;
        // Renewing-soon should also imply active.
        params.status = "active";
      }
      const { data } = await api.get("/subscriptions/", { params });
      setList(data || []);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, renewingBefore]);

  const setStatusFilter = (s) => {
    setStatus(s);
    const next = new URLSearchParams(params);
    if (s) next.set("status", s);
    else next.delete("status");
    next.delete("renewing_soon");
    setParams(next, { replace: true });
  };

  if (!office) {
    return (
      <Layout title="Subscriptions" subtitle="Billing">
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          You don't have access to the full subscriptions list. Open a customer
          to see their plan summary.
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Subscriptions" subtitle="Billing">
      <EntityHeader
        overline="recurring"
        title="Subscriptions"
        subtitle="Active protection-plan enrollments and lifecycle history."
      />

      <div className="mb-6">
        <MrrTile />
      </div>

      <div
        className="flex flex-wrap items-center gap-2 mb-4"
        data-testid="subscriptions-filters"
      >
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value || "all"}
            type="button"
            onClick={() => setStatusFilter(f.value)}
            data-testid={`filter-${f.value || "all"}`}
            className={`inline-flex items-center rounded-md border px-3 py-1.5 text-[12px] font-semibold transition-colors ${
              !renewingSoon && status === f.value
                ? "border-amber-500 bg-amber-50 text-amber-900"
                : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
            }`}
          >
            {f.label}
          </button>
        ))}
        {renewingSoon && (
          <span
            data-testid="renewing-soon-chip"
            className="inline-flex items-center rounded-md border border-sky-300 bg-sky-50 px-3 py-1.5 text-[12px] font-semibold text-sky-900"
          >
            Renewing in 30 days
            <button
              type="button"
              onClick={() => {
                const next = new URLSearchParams(params);
                next.delete("renewing_soon");
                setParams(next, { replace: true });
              }}
              className="ml-2 text-sky-700 hover:text-sky-900"
            >
              ×
            </button>
          </span>
        )}
      </div>

      <ErrorBanner message={err} />

      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white shadow-card">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-bold">
              <th className="px-5 py-3">Customer</th>
              <th className="px-5 py-3">Plan</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Billing</th>
              <th className="px-5 py-3 text-right">Amount</th>
              <th className="px-5 py-3">Period ends</th>
              <th className="px-5 py-3 text-right">MRR</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {list.length === 0 && (
              <EmptyState
                inTable
                colSpan={7}
                icon={CreditCardIcon}
                title="No subscriptions yet"
                description="Subscriptions are created when a customer enrolls in a Silver, Gold, or Platinum protection plan from their detail page."
                testId="subscriptions-empty"
              />
            )}
            {list.map((s) => (
              <tr
                key={s.id}
                onClick={() => nav(`/subscriptions/${s.id}`)}
                className="cursor-pointer hover:bg-slate-50/60"
                data-testid={`subscription-row-${s.id}`}
              >
                <td className="px-5 py-3 font-medium text-slate-900">
                  {s.customer_name || "—"}
                  {s.property_address && (
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {s.property_address}
                    </div>
                  )}
                </td>
                <td className="px-5 py-3 text-xs text-slate-700">
                  <div className="font-medium text-slate-900">{s.plan_name}</div>
                  <div className="font-data text-[10px] text-slate-500 mt-0.5">
                    {s.plan_code}
                  </div>
                </td>
                <td className="px-5 py-3">
                  <SubscriptionStatusBadge status={s.status} />
                  {s.cancel_at_period_end && (
                    <div className="text-[10px] text-rose-700 mt-1">
                      Cancels at period end
                    </div>
                  )}
                </td>
                <td className="px-5 py-3">
                  <BillingStatusBadge status={s.billing_status} />
                  {s.billing_mode === "stripe" && (
                    <div className="text-[10px] text-slate-500 mt-1 font-data">
                      Stripe
                    </div>
                  )}
                </td>
                <td className="px-5 py-3 font-data text-xs text-slate-900 text-right">
                  {fmtCents(s.amount_cents)}{" "}
                  <span className="text-slate-500">
                    {CADENCE_LABEL[s.billing_cadence] || ""}
                  </span>
                </td>
                <td className="px-5 py-3 font-data text-xs text-slate-700">
                  {s.current_period_end || "—"}
                </td>
                <td className="px-5 py-3 font-data text-xs text-slate-900 text-right">
                  {s.status === "active"
                    ? fmtCents(s.mrr_contribution_cents)
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
